import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  UtilCategoryFormRule,
  UtilCategoryFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultUtilCategory } from "@factory/form/form-default-util-category.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseUtilCategoryModel } from "@factory/model/query-model-util-category";
import {
  useQyDeleteUtilCategory,
  useQyGetUtilCategoryById,
  useQyUpdateUtilCategory,
} from "@factory/query/util-category.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditUtilCategory() {
  // LOCAL DECLARATION
  const { getDefault } = useFormDefaultUtilCategory();
  const navigate = useNavigate();
  const { mapUpdateUtilCategory, mapDeleteUtilCategory } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { utilCategoryId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID UtilCategory
  const handleGetApiSuccess = (data: GetResponseUtilCategoryModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("tenant", responseData?.tenant || "");
    setValue("name", responseData?.name || "");
    setValue("description", responseData?.description || "");
  };
  const {
    data: utilCategoryResponse,
    isLoading,
    isError: utilCategoryError,
  } = useQyGetUtilCategoryById(utilCategoryId || "", handleGetApiSuccess);
  const utilCategoryData = utilCategoryResponse?.data?.[0];

  // API UPDATE UtilCategory
  const handleUpdateApiSuccess = () => {
    showSuccess("UtilCategory updated successfully");
  };
  const { mutate: updateUtilCategory, isLoading: isEditLoading } =
    useQyUpdateUtilCategory(handleUpdateApiSuccess);

  // API DELETE UtilCategory
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("UtilCategory deleted successfully");
  };
  const { mutate: deleteUtilCategory } = useQyDeleteUtilCategory(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<UtilCategoryFormSchema>({
    defaultValues: getDefault(utilCategoryData),
    resolver: zodResolver(UtilCategoryFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: UtilCategoryFormSchema) => {
    if (!utilCategoryData || !utilCategoryData.code) {
      throw new Error("No utilCategory data");
    }

    const formData = mapUpdateUtilCategory(form, utilCategoryData.code);
    updateUtilCategory(formData);
  };
  const handleValidateError = (err: FieldErrors<UtilCategoryFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!utilCategoryData || !utilCategoryData.code) {
      throw new Error("No utilCategory data found");
    }

    const form = mapDeleteUtilCategory(utilCategoryData.code);
    deleteUtilCategory(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    utilCategoryId,
    utilCategoryData,
    isLoading,
    utilCategoryError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
